import React, { useMemo } from 'react';
import range from 'lodash/range';
import type { GridElement } from '../types';
import gridStyle from './GridRow.scss';

export interface Props {
  start: number;
  end: number;
  elementWidth: number;
  elementHeight: number;
  elementSpacing: number;
  withShadowSpace?: boolean;
  renderElement: (element: GridElement) => React.ReactNode;
}

const VisibleGridRow: React.FC<Props> = ({
  start,
  end,
  elementWidth,
  elementHeight,
  elementSpacing,
  withShadowSpace = true,
  renderElement,
}) => {
  const inlineStyles = useMemo(() => {
    const rowWidth =
      (elementWidth + elementSpacing) * (end - start) - elementSpacing;

    const styles: React.CSSProperties = {
      width: rowWidth,
      ['containIntrinsicSize' as any]: `${rowWidth}px ${elementHeight}px`,
      ['contentVisibility' as any]: 'auto',
    };

    // create space around contents for shadows, ribbons, etc.
    // but draw it back in with negative margin so bounds match width
    if (withShadowSpace) {
      styles.margin = `-${elementSpacing}px`;
      styles.padding = `${elementSpacing}px`;
    } else if (start > 0) {
      // gap between rows
      styles.marginTop = `${elementSpacing}px`;
    }

    return styles;
  }, [
    elementHeight,
    elementSpacing,
    elementWidth,
    end,
    start,
    withShadowSpace,
  ]);

  return (
    <div className={gridStyle['row']} style={inlineStyles}>
      {range(start, end).map(index => (
        <React.Fragment key={index}>
          {renderElement({
            width: elementWidth,
            height: elementHeight,
            index,
            style: {
              width: elementWidth,
              height: elementHeight,
              marginLeft: index > start ? elementSpacing : 0,
            },
            onscreen: true,
            visible: true,
          })}
        </React.Fragment>
      ))}
    </div>
  );
};
VisibleGridRow.displayName = 'VisibleGridRow';

export default VisibleGridRow;
